import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react'
import toast from 'react-hot-toast'
import { api } from '../configs/api'

interface UserType {
    userID: number
    name: string
    email: string
    admin: boolean
    image?: string
}

interface UnityType {
    unityID: number
    name: string
}

interface AuthContextData {
    user: UserType | null
    unity: UnityType | null
    token: string | null
    signIn: (email: string, password: string) => Promise<{ success: boolean; message: string }>
    signOut: () => void
    loadUsers: () => Promise<void>
    users: {
        id: number
        name: string
        email?: string
    }[]
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<UserType | null>(null)
    const [users, setUsers] = useState<any[] | []>([])
    const [unity, setUnity] = useState<UnityType | null>(null)
    const [token, setToken] = useState<string | null>(null)

    useEffect(() => {
        const storagedToken = localStorage.getItem('@crm-auth')
        const storagedUser = localStorage.getItem('@crm-user')
        const storagedUnity = localStorage.getItem('@crm-unity')
        if (storagedToken) {
            setToken(storagedToken)
        }
        if (storagedUser) {
            setUser(JSON.parse(storagedUser))
        }
        if (storagedUnity) {
            setUnity(JSON.parse(storagedUnity))
            getUsers(JSON.parse(storagedUnity).unityID)
        }
    }, [])

    const getUsers = async (unityID: number) => {
        try {
            const response = await fetch(`${api}auth/getUsers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ unityID })
            })

            const result = await response.json()
            setUsers(result.list || [])
        } catch (error) {
            console.error('Erro ao buscar usuários', error)
        }
    }

    const loadUsers = async () => {
        if (unity) await getUsers(unity.unityID)
    }

    const signIn = async (email: string, password: string) => {
        try {
            if (!email || !password) {
                toast.error('Preencha todos os campos')
                return { success: false, message: 'Preencha todos os campos' }
            }

            const response = await fetch(`${api}auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            })
            const result = await response.json()

            if (!result || !result.success) {
                return { success: false, message: result.message }
            }

            //* Token
            const token = result.token
            localStorage.setItem('@crm-auth', token)
            setToken(token)

            //* User
            const user = result.user
            localStorage.setItem('@crm-user', JSON.stringify(user))
            setUser(user)

            //* Unity
            const unity = result.unity
            localStorage.setItem('@crm-unity', JSON.stringify(unity))
            setUnity(unity)

            await getUsers(unity.unityID)

            return { success: true, message: 'Login realizado com sucesso' }
        } catch (error) {
            console.error('Erro no login', error)
            throw new Error('Falha na autenticação')
        }
    }

    const signOut = () => {
        localStorage.removeItem('@crm-auth')
        setToken(null)
        localStorage.removeItem('@crm-user')
        setUser(null)
        localStorage.removeItem('@crm-unity')
        setUnity(null)
    }

    return (
        <AuthContext.Provider value={{ user, unity, token, signIn, signOut, users, loadUsers }}>{children}</AuthContext.Provider>
    )
}

export function useAuth() {
    return useContext(AuthContext)
}
